import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCancelEventApplicationErrors,
  BulkCancelEventOptions,
  BulkCancelEventResponse,
  BulkCreateEventOptions,
  BulkCreateEventResponse,
  BulkUpdateEventOptions,
  BulkUpdateEventRequestMaskedEvent,
  BulkUpdateEventResponse,
  CancelEventApplicationErrors,
  CancelEventOptions,
  CancelEventResponse,
  CreateEventOptions,
  Event,
  EventCancelledEnvelope,
  EventCreatedEnvelope,
  EventQuery,
  EventRecurringSplitEnvelope,
  EventUpdatedEnvelope,
  EventsQueryBuilder,
  FieldWithLiterals,
  GetEventOptions,
  ListEventsByContactIdOptions,
  ListEventsByContactIdResponse,
  ListEventsByMemberIdOptions,
  ListEventsByMemberIdResponse,
  ListEventsOptions,
  ListEventsResponse,
  MaskedEvent,
  QueryEventsOptions,
  QueryEventsResponse,
  RestoreEventDefaultsOptions,
  RestoreEventDefaultsResponse,
  SplitRecurringEventOptions,
  SplitRecurringEventResponse,
  UpdateEvent,
  UpdateEventOptions,
  bulkCancelEvent as universalBulkCancelEvent,
  bulkCreateEvent as universalBulkCreateEvent,
  bulkUpdateEvent as universalBulkUpdateEvent,
  cancelEvent as universalCancelEvent,
  createEvent as universalCreateEvent,
  getEvent as universalGetEvent,
  listEvents as universalListEvents,
  listEventsByContactId as universalListEventsByContactId,
  listEventsByMemberId as universalListEventsByMemberId,
  queryEvents as universalQueryEvents,
  restoreEventDefaults as universalRestoreEventDefaults,
  splitRecurringEvent as universalSplitRecurringEvent,
  typedQueryEvents as universalTypedQueryEvents,
  updateEvent as universalUpdateEvent,
} from './calendar-v3-event-events.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/calendar' };

export function listEventsByContactId(
  httpClient: HttpClient
): ListEventsByContactIdSignature {
  return (contactId: string, options?: ListEventsByContactIdOptions) =>
    universalListEventsByContactId(
      contactId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEventsByContactIdSignature {
  /**
   * Retrieves a list of events filtered by the participant's
   * [contact](https://dev.wix.com/docs/sdk/backend-modules/crm/contacts/introduction)
   * ID.
   *
   *
   * You can't set `toLocalDate` to a value that's more than a full year after
   * `fromLocalDate`.
   * @param - ID of the [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)
   * to retrieve events for. Required, unless you provide `cursorPaging`.
   */
  (contactId: string, options?: ListEventsByContactIdOptions): Promise<
    NonNullablePaths<
      ListEventsByContactIdResponse,
      | `events`
      | `events.${number}.status`
      | `events.${number}.recurrenceType`
      | `events.${number}.recurrenceRule.frequency`
      | `events.${number}.transparency`
      | `events.${number}.location.type`
      | `events.${number}.participants.status`
      | `events.${number}.conferencingDetails.type`,
      5
    >
  >;
}

export function listEventsByMemberId(
  httpClient: HttpClient
): ListEventsByMemberIdSignature {
  return (memberId: string, options?: ListEventsByMemberIdOptions) =>
    universalListEventsByMemberId(
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEventsByMemberIdSignature {
  /**
   * Retrieves a list of events filtered by the participant's
   * [member](https://dev.wix.com/docs/sdk/backend-modules/members/members/introduction)
   * ID.
   *
   *
   * You can't set `toLocalDate` to a value that's more than a full year after
   * `fromLocalDate`.
   * @param - ID of the [member](https://dev.wix.com/docs/rest/crm/members-contacts/members/members/member-object)
   * to retrieve events for. Required, unless you provide `cursorPaging`.
   * Provide `me` to retrieve events for the currently logged-in member.
   * You must have the `Read Calendars - Including PI`
   * [permission scope](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/configure-permissions-for-your-app)
   * to retrieve events for members who aren't the currently logged in member.
   */
  (memberId: string, options?: ListEventsByMemberIdOptions): Promise<
    NonNullablePaths<
      ListEventsByMemberIdResponse,
      | `events`
      | `events.${number}.status`
      | `events.${number}.recurrenceType`
      | `events.${number}.recurrenceRule.frequency`
      | `events.${number}.transparency`
      | `events.${number}.location.type`
      | `events.${number}.participants.status`
      | `events.${number}.conferencingDetails.type`,
      5
    >
  >;
}

export function getEvent(httpClient: HttpClient): GetEventSignature {
  return (eventId: string, options?: GetEventOptions) =>
    universalGetEvent(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEventSignature {
  /**
   * Retrieves an event.
   * @param - ID of the event to retrieve.
   *
   * Min: 36 characters
   * Max: 250 characters
   * @returns Retrieved event.
   */
  (eventId: string, options?: GetEventOptions): Promise<
    NonNullablePaths<
      Event,
      | `status`
      | `recurrenceType`
      | `recurrenceRule.frequency`
      | `recurrenceRule.days`
      | `transparency`
      | `location.type`
      | `resources`
      | `resources.${number}.transparency`
      | `resources.${number}.permissionRole`
      | `participants.list`
      | `participants.status`
      | `conferencingDetails.type`
      | `inheritedFields`
      | `permissions`
      | `permissions.${number}.identity.wixUserId`
      | `permissions.${number}.role`,
      5
    >
  >;
}

export function listEvents(httpClient: HttpClient): ListEventsSignature {
  return (eventIds: string[], options?: ListEventsOptions) =>
    universalListEvents(
      eventIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEventsSignature {
  /**
   * Retrieves a list of events by their IDs.
   * @param - IDs of the events to retrieve.
   *
   * Min: 1 event ID
   * Max: 100 event IDs
   */
  (eventIds: string[], options?: ListEventsOptions): Promise<
    NonNullablePaths<
      ListEventsResponse,
      | `events`
      | `events.${number}.status`
      | `events.${number}.recurrenceType`
      | `events.${number}.recurrenceRule.frequency`
      | `events.${number}.transparency`
      | `events.${number}.location.type`
      | `events.${number}.participants.status`
      | `events.${number}.conferencingDetails.type`,
      5
    >
  >;
}

export function queryEvents(httpClient: HttpClient): QueryEventsSignature {
  return (options?: QueryEventsOptions) =>
    universalQueryEvents(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryEventsSignature {
  /**
   * Creates a query to retrieve a list of events.
   *
   * The `queryEvents()` function builds a query to retrieve a list of events and returns an `EventsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/events-query-builder/find) function.
   *
   * You can refine the query by chaining `EventsQueryBuilder` functions onto the query. `EventsQueryBuilder` functions enable you to sort, filter, and control the results that `queryEvents()` returns.
   *
   * `queryEvents()` runs with the following `EventsQueryBuilder` default that you can override:
   *
   * + `limit` is `50`.
   * + Sorted by `event.start` in ascending order.
   *
   * The functions that are chained to `queryEvents()` are applied in the order they are called.
   *
   * The following `EventsQueryBuilder` functions are supported for the `queryEvents()` function. For a full description of the events object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/events-query-result/items) property in `EventsQueryResult`.
   *
   */
  (options?: QueryEventsOptions): EventsQueryBuilder;
}

export function typedQueryEvents(
  httpClient: HttpClient
): TypedQueryEventsSignature {
  return (query: EventQuery, options?: QueryEventsOptions) =>
    universalTypedQueryEvents(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryEventsSignature {
  /** */
  (query: EventQuery, options?: QueryEventsOptions): Promise<
    NonNullablePaths<
      QueryEventsResponse,
      | `events`
      | `events.${number}.status`
      | `events.${number}.recurrenceType`
      | `events.${number}.recurrenceRule.frequency`
      | `events.${number}.transparency`
      | `events.${number}.location.type`
      | `events.${number}.participants.status`
      | `events.${number}.conferencingDetails.type`,
      5
    >
  >;
}

export function createEvent(httpClient: HttpClient): CreateEventSignature {
  return (
    event: NonNullablePaths<
      Event,
      | `conferencingDetails.guestUrl`
      | `conferencingDetails.hostUrl`
      | `end`
      | `end.localDate`
      | `location.type`
      | `participants.list.${number}.name`
      | `recurrenceRule.days`
      | `recurrenceRule.frequency`
      | `recurrenceRule.until.localDate`
      | `resources.${number}._id`
      | `scheduleId`
      | `start`
      | `start.localDate`,
      5
    >,
    options?: CreateEventOptions
  ) =>
    universalCreateEvent(
      event,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateEventSignature {
  /**
   * Creates an event.
   *
   *
   * If you provide `recurrenceRule`, an event with `{"recurrenceType": "MASTER"}` is created. Then, you can't set `start.localDate` to a past date, though the time can be earlier on the same day. You can, however, create single-occurrence events for past dates.
   *
   * You can't create an event with `recurrenceType` set to `INSTANCE` or `EXCEPTION`. `INSTANCE` events are generated automatically based on the recurrence rule in the relevant `MASTER` event. `EXCEPTION` events are automatically created when you [update](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event) an `INSTANCE` event, which changes its `recurrenceType` to `EXCEPTION`.
   * @param - Event to create.
   * @returns Created event.
   */
  (
    event: NonNullablePaths<
      Event,
      | `conferencingDetails.guestUrl`
      | `conferencingDetails.hostUrl`
      | `end`
      | `end.localDate`
      | `location.type`
      | `participants.list.${number}.name`
      | `recurrenceRule.days`
      | `recurrenceRule.frequency`
      | `recurrenceRule.until.localDate`
      | `resources.${number}._id`
      | `scheduleId`
      | `start`
      | `start.localDate`,
      5
    >,
    options?: CreateEventOptions
  ): Promise<
    NonNullablePaths<
      Event,
      | `status`
      | `recurrenceType`
      | `recurrenceRule.frequency`
      | `recurrenceRule.days`
      | `transparency`
      | `location.type`
      | `resources`
      | `resources.${number}.transparency`
      | `resources.${number}.permissionRole`
      | `participants.list`
      | `participants.status`
      | `conferencingDetails.type`
      | `inheritedFields`
      | `permissions`
      | `permissions.${number}.identity.wixUserId`
      | `permissions.${number}.role`,
      5
    >
  >;
}

export function bulkCreateEvent(
  httpClient: HttpClient
): BulkCreateEventSignature {
  return (
    events: NonNullablePaths<
      MaskedEvent,
      | `event`
      | `event.conferencingDetails.guestUrl`
      | `event.conferencingDetails.hostUrl`
      | `event.end`
      | `event.end.localDate`
      | `event.location.type`
      | `event.participants.list.${number}.name`
      | `event.recurrenceRule.days`
      | `event.recurrenceRule.frequency`
      | `event.recurrenceRule.until.localDate`
      | `event.resources.${number}._id`
      | `event.scheduleId`
      | `event.start`
      | `event.start.localDate`,
      6
    >[],
    options?: BulkCreateEventOptions
  ) =>
    universalBulkCreateEvent(
      events,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateEventSignature {
  /**
   * Creates multiple events in bulk.
   *
   *
   * If you provide `recurrenceRule`, an event with `{"recurrenceType": "MASTER"}` is
   * created. Then, you can't set `start.localDate` to a past date, though the time can be
   * earlier on the same day. You can, however, create single-occurrence events for
   * past dates.
   *
   * You can't create an event with `recurrenceType` set to `INSTANCE` or `EXCEPTION`.
   * `INSTANCE` events are generated automatically based on the recurrence rule in the
   * relevant `MASTER` event. `EXCEPTION` events are automatically created when you
   * [update](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event)
   * an `INSTANCE` event, which changes its `recurrenceType` to `EXCEPTION`.
   * @param - Events to create.
   */
  (
    events: NonNullablePaths<
      MaskedEvent,
      | `event`
      | `event.conferencingDetails.guestUrl`
      | `event.conferencingDetails.hostUrl`
      | `event.end`
      | `event.end.localDate`
      | `event.location.type`
      | `event.participants.list.${number}.name`
      | `event.recurrenceRule.days`
      | `event.recurrenceRule.frequency`
      | `event.recurrenceRule.until.localDate`
      | `event.resources.${number}._id`
      | `event.scheduleId`
      | `event.start`
      | `event.start.localDate`,
      6
    >[],
    options?: BulkCreateEventOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateEventResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.status`
      | `results.${number}.item.recurrenceType`
      | `results.${number}.item.recurrenceRule.frequency`
      | `results.${number}.item.transparency`
      | `results.${number}.item.location.type`
      | `results.${number}.item.participants.status`
      | `results.${number}.item.conferencingDetails.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`,
      6
    >
  >;
}

export function updateEvent(httpClient: HttpClient): UpdateEventSignature {
  return (
    _id: string,
    event: NonNullablePaths<
      UpdateEvent,
      | `conferencingDetails.guestUrl`
      | `conferencingDetails.hostUrl`
      | `end.localDate`
      | `location.type`
      | `participants.list.${number}.name`
      | `recurrenceRule.days`
      | `recurrenceRule.frequency`
      | `recurrenceRule.until.localDate`
      | `resources.${number}._id`
      | `revision`
      | `start.localDate`,
      5
    >,
    options?: UpdateEventOptions
  ) =>
    universalUpdateEvent(
      _id,
      event,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateEventSignature {
  /**
   * Updates an event.
   *
   *
   * When updating an event with `{"recurrenceType": "INSTANCE"}`, the
   * `recurrenceType` automatically changes to `EXCEPTION`.
   *
   * When updating an event with `{"recurrenceType": "MASTER"}`:
   * + Past `INSTANCE` and `EXCEPTION` events remain unchanged.
   * + For future `INSTANCE` events, all relevant fields are updated.
   * + For future `EXCEPTION` events, only changes to the `inheritedFields` are
   * applied, while other fields remain unchanged.
   * @param - Event ID.
   * @returns Updated event.
   */
  (
    _id: string,
    event: NonNullablePaths<
      UpdateEvent,
      | `conferencingDetails.guestUrl`
      | `conferencingDetails.hostUrl`
      | `end.localDate`
      | `location.type`
      | `participants.list.${number}.name`
      | `recurrenceRule.days`
      | `recurrenceRule.frequency`
      | `recurrenceRule.until.localDate`
      | `resources.${number}._id`
      | `revision`
      | `start.localDate`,
      5
    >,
    options?: UpdateEventOptions
  ): Promise<
    NonNullablePaths<
      Event,
      | `status`
      | `recurrenceType`
      | `recurrenceRule.frequency`
      | `recurrenceRule.days`
      | `transparency`
      | `location.type`
      | `resources`
      | `resources.${number}.transparency`
      | `resources.${number}.permissionRole`
      | `participants.list`
      | `participants.status`
      | `conferencingDetails.type`
      | `inheritedFields`
      | `permissions`
      | `permissions.${number}.identity.wixUserId`
      | `permissions.${number}.role`,
      5
    >
  >;
}

export function bulkUpdateEvent(
  httpClient: HttpClient
): BulkUpdateEventSignature {
  return (
    events: NonNullablePaths<
      BulkUpdateEventRequestMaskedEvent,
      | `event`
      | `event._id`
      | `event.conferencingDetails.guestUrl`
      | `event.conferencingDetails.hostUrl`
      | `event.end.localDate`
      | `event.location.type`
      | `event.participants.list.${number}.name`
      | `event.recurrenceRule.days`
      | `event.recurrenceRule.frequency`
      | `event.recurrenceRule.until.localDate`
      | `event.resources.${number}._id`
      | `event.revision`
      | `event.start.localDate`,
      6
    >[],
    options?: BulkUpdateEventOptions
  ) =>
    universalBulkUpdateEvent(
      events,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateEventSignature {
  /**
   * Updates multiple events in bulk.
   *
   *
   * When updating an event with `{"recurrenceType": "INSTANCE"}`, the
   * `recurrenceType` automatically changes to `EXCEPTION`.
   *
   * When updating an event with `{"recurrenceType": "MASTER"}`:
   * + Past `INSTANCE` and `EXCEPTION` events remain unchanged.
   * + For future `INSTANCE` events, all relevant fields are updated.
   * + For future `EXCEPTION` events, only changes to the `inheritedFields` are
   * applied, while other fields remain unchanged.
   * @param - Events to update.
   *
   * Min: 1 event
   * Max: 50 events
   */
  (
    events: NonNullablePaths<
      BulkUpdateEventRequestMaskedEvent,
      | `event`
      | `event._id`
      | `event.conferencingDetails.guestUrl`
      | `event.conferencingDetails.hostUrl`
      | `event.end.localDate`
      | `event.location.type`
      | `event.participants.list.${number}.name`
      | `event.recurrenceRule.days`
      | `event.recurrenceRule.frequency`
      | `event.recurrenceRule.until.localDate`
      | `event.resources.${number}._id`
      | `event.revision`
      | `event.start.localDate`,
      6
    >[],
    options?: BulkUpdateEventOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateEventResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.status`
      | `results.${number}.item.recurrenceType`
      | `results.${number}.item.recurrenceRule.frequency`
      | `results.${number}.item.transparency`
      | `results.${number}.item.location.type`
      | `results.${number}.item.participants.status`
      | `results.${number}.item.conferencingDetails.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`,
      6
    >
  >;
}

export function restoreEventDefaults(
  httpClient: HttpClient
): RestoreEventDefaultsSignature {
  return (
    eventId: string,
    fields: FieldWithLiterals[],
    options?: RestoreEventDefaultsOptions
  ) =>
    universalRestoreEventDefaults(
      eventId,
      fields,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreEventDefaultsSignature {
  /**
   * Restores the event's default values from the relevant schedule or `MASTER`
   * event.
   *
   *
   * You must provide `fields` to specify which `inheritedFields` are restored.
   *
   * For example, if you've created an event that takes place in a non-default
   * location with a custom title and capacity, but you want to reset only title
   * and capacity to default values without affecting the location, provide the
   * event ID and set `fields` to `["TITLE", "CAPACITY"]`.
   *
   * Refer to the [default values article](https://dev.wix.com/docs/sdk/backend-modules/calendar/default-values)
   * for more information.
   * @param - ID of the event for which to restore default values.
   * @param - Fields for which to restore default values.
   *
   * `TIME` restores default values for `start` and `end`.
   *
   * Min: 1 field
   */
  (
    eventId: string,
    fields: FieldWithLiterals[],
    options?: RestoreEventDefaultsOptions
  ): Promise<
    NonNullablePaths<
      RestoreEventDefaultsResponse,
      | `event.status`
      | `event.recurrenceType`
      | `event.recurrenceRule.frequency`
      | `event.recurrenceRule.days`
      | `event.transparency`
      | `event.location.type`
      | `event.resources`
      | `event.resources.${number}.transparency`
      | `event.resources.${number}.permissionRole`
      | `event.participants.list`
      | `event.participants.status`
      | `event.conferencingDetails.type`
      | `event.inheritedFields`
      | `event.permissions`
      | `event.permissions.${number}.identity.wixUserId`
      | `event.permissions.${number}.role`,
      6
    >
  >;
}

export function splitRecurringEvent(
  httpClient: HttpClient
): SplitRecurringEventSignature {
  return (
    recurringEventId: string,
    splitLocalDate: string,
    options?: SplitRecurringEventOptions
  ) =>
    universalSplitRecurringEvent(
      recurringEventId,
      splitLocalDate,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SplitRecurringEventSignature {
  /**
   * Splits a recurring `MASTER` event into 2 separate `MASTER` events.
   *
   *
   * You must provide a `splitLocalDate` that's in the future and after the `start`
   * date of the next `INSTANCE` or `EXCEPTION` event in the series. Additionally,
   * there must be another `INSTANCE` or `EXCEPTION` event following this next event,
   * as the changes wouldn't affect any event without a subsequent occurrence.
   *
   * If you want to modify a `MASTER` event with the changes already applying to
   * the very next event, use
   * [updateEvent()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event)
   * instead, as this preserves past events and only modifies future events.
   *
   * The main consequences are:
   * - **The original `MASTER` event is shortened**. Its `recurrenceRule.until.localDate`
   * is updated to `end.localDate` of the latest `INSTANCE` or `EXCEPTION`
   * event starting before `splitLocalDate`. If an `INSTANCE` or `EXCEPTION` event
   * starts before but ends after `splitLocalDate`, `until.localDate` is set
   * to value that's later than `splitLocalDate`.
   * - **A new `MASTER` event is created**, starting from the first event that
   * begins after `splitLocalDate`.
   * - **Existing `INSTANCE` and `EXCEPTION` events that occur after the split are updated**.
   * Their `recurringEventId` is set to the ID of the newly created `MASTER` event.
   * @param - ID of the `MASTER` event to split.
   * @param - Local date and time at which the `MASTER` event is split in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2025-03-11T09:00:00`.
   * Must be a future date that's after the `start` date of the next `INSTANCE` or
   * `EXCEPTION` event in the series. Additionally, there must be another
   * `INSTANCE` or `EXCEPTION` event following this next event, as the changes
   * wouldn't affect any event without a subsequent occurrence.
   */
  (
    recurringEventId: string,
    splitLocalDate: string,
    options?: SplitRecurringEventOptions
  ): Promise<
    NonNullablePaths<
      SplitRecurringEventResponse,
      | `updatedRecurringEventEndingBeforeSplit.status`
      | `updatedRecurringEventEndingBeforeSplit.recurrenceType`
      | `updatedRecurringEventEndingBeforeSplit.recurrenceRule.frequency`
      | `updatedRecurringEventEndingBeforeSplit.recurrenceRule.days`
      | `updatedRecurringEventEndingBeforeSplit.transparency`
      | `updatedRecurringEventEndingBeforeSplit.location.type`
      | `updatedRecurringEventEndingBeforeSplit.resources`
      | `updatedRecurringEventEndingBeforeSplit.resources.${number}.transparency`
      | `updatedRecurringEventEndingBeforeSplit.resources.${number}.permissionRole`
      | `updatedRecurringEventEndingBeforeSplit.participants.list`
      | `updatedRecurringEventEndingBeforeSplit.participants.status`
      | `updatedRecurringEventEndingBeforeSplit.conferencingDetails.type`
      | `updatedRecurringEventEndingBeforeSplit.inheritedFields`
      | `updatedRecurringEventEndingBeforeSplit.permissions`
      | `updatedRecurringEventEndingBeforeSplit.permissions.${number}.identity.wixUserId`
      | `updatedRecurringEventEndingBeforeSplit.permissions.${number}.role`
      | `newRecurringEventStartingFromSplit.status`
      | `newRecurringEventStartingFromSplit.recurrenceType`
      | `newRecurringEventStartingFromSplit.recurrenceRule.frequency`
      | `newRecurringEventStartingFromSplit.recurrenceRule.days`
      | `newRecurringEventStartingFromSplit.transparency`
      | `newRecurringEventStartingFromSplit.location.type`
      | `newRecurringEventStartingFromSplit.resources`
      | `newRecurringEventStartingFromSplit.resources.${number}.transparency`
      | `newRecurringEventStartingFromSplit.resources.${number}.permissionRole`
      | `newRecurringEventStartingFromSplit.participants.list`
      | `newRecurringEventStartingFromSplit.participants.status`
      | `newRecurringEventStartingFromSplit.conferencingDetails.type`
      | `newRecurringEventStartingFromSplit.inheritedFields`
      | `newRecurringEventStartingFromSplit.permissions`
      | `newRecurringEventStartingFromSplit.permissions.${number}.identity.wixUserId`
      | `newRecurringEventStartingFromSplit.permissions.${number}.role`,
      6
    >
  >;
}

export function cancelEvent(httpClient: HttpClient): CancelEventSignature {
  return (eventId: string, options?: CancelEventOptions) =>
    universalCancelEvent(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelEventSignature {
  /**
   * Cancels an event.
   * @param - ID of the event to cancel.
   */
  (eventId: string, options?: CancelEventOptions): Promise<
    NonNullablePaths<
      CancelEventResponse,
      | `event.status`
      | `event.recurrenceType`
      | `event.recurrenceRule.frequency`
      | `event.recurrenceRule.days`
      | `event.transparency`
      | `event.location.type`
      | `event.resources`
      | `event.resources.${number}.transparency`
      | `event.resources.${number}.permissionRole`
      | `event.participants.list`
      | `event.participants.status`
      | `event.conferencingDetails.type`
      | `event.inheritedFields`
      | `event.permissions`
      | `event.permissions.${number}.identity.wixUserId`
      | `event.permissions.${number}.role`,
      6
    > & {
      __applicationErrorsType?: CancelEventApplicationErrors;
    }
  >;
}

export function bulkCancelEvent(
  httpClient: HttpClient
): BulkCancelEventSignature {
  return (eventIds: string[], options?: BulkCancelEventOptions) =>
    universalBulkCancelEvent(
      eventIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCancelEventSignature {
  /**
   * Cancels multiple events in bulk.
   * @param - IDs of the events to cancel.
   */
  (eventIds: string[], options?: BulkCancelEventOptions): Promise<
    NonNullablePaths<
      BulkCancelEventResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.status`
      | `results.${number}.item.recurrenceType`
      | `results.${number}.item.recurrenceRule.frequency`
      | `results.${number}.item.transparency`
      | `results.${number}.item.location.type`
      | `results.${number}.item.participants.status`
      | `results.${number}.item.conferencingDetails.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`,
      6
    > & {
      __applicationErrorsType?: BulkCancelEventApplicationErrors;
    }
  >;
}

export const onEventCancelled = EventDefinition(
  'wix.calendar.v3.event_cancelled',
  true,
  (event: EventCancelledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.event.createdDate' },
            { path: 'data.event.updatedDate' },
            { path: 'data.event.start.utcDate' },
            { path: 'data.event.end.utcDate' },
            { path: 'data.event.adjustedStart.utcDate' },
            { path: 'data.event.adjustedEnd.utcDate' },
            { path: 'data.event.recurrenceRule.until.utcDate' },
            { path: 'data.event.recurrenceRule.adjustedUntil.utcDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EventCancelledEnvelope>();
export const onEventCreated = EventDefinition(
  'wix.calendar.v3.event_created',
  true,
  (event: EventCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.start.utcDate' },
            { path: 'entity.end.utcDate' },
            { path: 'entity.adjustedStart.utcDate' },
            { path: 'entity.adjustedEnd.utcDate' },
            { path: 'entity.recurrenceRule.until.utcDate' },
            { path: 'entity.recurrenceRule.adjustedUntil.utcDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EventCreatedEnvelope>();
export const onEventRecurringSplit = EventDefinition(
  'wix.calendar.v3.event_recurring_split',
  true,
  (event: EventRecurringSplitEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.updatedRecurringEventEndingBeforeSplit.createdDate' },
            { path: 'data.updatedRecurringEventEndingBeforeSplit.updatedDate' },
            {
              path: 'data.updatedRecurringEventEndingBeforeSplit.start.utcDate',
            },
            { path: 'data.updatedRecurringEventEndingBeforeSplit.end.utcDate' },
            {
              path: 'data.updatedRecurringEventEndingBeforeSplit.adjustedStart.utcDate',
            },
            {
              path: 'data.updatedRecurringEventEndingBeforeSplit.adjustedEnd.utcDate',
            },
            {
              path: 'data.updatedRecurringEventEndingBeforeSplit.recurrenceRule.until.utcDate',
            },
            {
              path: 'data.updatedRecurringEventEndingBeforeSplit.recurrenceRule.adjustedUntil.utcDate',
            },
            { path: 'data.newRecurringEventStartingFromSplit.createdDate' },
            { path: 'data.newRecurringEventStartingFromSplit.updatedDate' },
            { path: 'data.newRecurringEventStartingFromSplit.start.utcDate' },
            { path: 'data.newRecurringEventStartingFromSplit.end.utcDate' },
            {
              path: 'data.newRecurringEventStartingFromSplit.adjustedStart.utcDate',
            },
            {
              path: 'data.newRecurringEventStartingFromSplit.adjustedEnd.utcDate',
            },
            {
              path: 'data.newRecurringEventStartingFromSplit.recurrenceRule.until.utcDate',
            },
            {
              path: 'data.newRecurringEventStartingFromSplit.recurrenceRule.adjustedUntil.utcDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EventRecurringSplitEnvelope>();
export const onEventUpdated = EventDefinition(
  'wix.calendar.v3.event_updated',
  true,
  (event: EventUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.start.utcDate' },
            { path: 'entity.end.utcDate' },
            { path: 'entity.adjustedStart.utcDate' },
            { path: 'entity.adjustedEnd.utcDate' },
            { path: 'entity.recurrenceRule.until.utcDate' },
            { path: 'entity.recurrenceRule.adjustedUntil.utcDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.start.utcDate' },
            { path: 'modifiedFields.end.utcDate' },
            { path: 'modifiedFields.adjustedStart.utcDate' },
            { path: 'modifiedFields.adjustedEnd.utcDate' },
            { path: 'modifiedFields.recurrenceRule.until.utcDate' },
            { path: 'modifiedFields.recurrenceRule.adjustedUntil.utcDate' },
          ],
        },
      ])
    )
)<EventUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCancelEventOptions,
  BulkCancelEventRequest,
  BulkCancelEventResponse,
  BulkCreateEventOptions,
  BulkCreateEventRequest,
  BulkCreateEventResponse,
  BulkEventResult,
  BulkUpdateEventOptions,
  BulkUpdateEventRequest,
  BulkUpdateEventRequestMaskedEvent,
  BulkUpdateEventResponse,
  BusinessSchedule,
  CancelEventOptions,
  CancelEventRequest,
  CancelEventResponse,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CommonCursorPaging,
  CommonCursorPagingMetadata,
  CommonCursors,
  CommonIdentificationData,
  CommonIdentificationDataIdOneOf,
  ConferencingDetails,
  ConsentPolicy,
  CreateEventOptions,
  CreateEventRequest,
  CreateEventResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  Day,
  DayOfWeek,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Event,
  EventCancelled,
  EventCancelledEnvelope,
  EventCreatedEnvelope,
  EventMetadata,
  EventQuerySpec,
  EventRecurringSplitEnvelope,
  EventUpdatedEnvelope,
  EventUpdatedWithMetadata,
  EventsQueryBuilder,
  EventsQueryResult,
  ExtendedFields,
  Field,
  Frequency,
  GeoCoordinates,
  GetEventOptions,
  GetEventRequest,
  GetEventResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  ItemMetadata,
  ListEventsByContactIdOptions,
  ListEventsByContactIdRequest,
  ListEventsByContactIdResponse,
  ListEventsByMemberIdOptions,
  ListEventsByMemberIdRequest,
  ListEventsByMemberIdResponse,
  ListEventsOptions,
  ListEventsRequest,
  ListEventsResponse,
  ListRecurringEventInstancesHistoryRequest,
  ListRecurringEventInstancesHistoryResponse,
  Locale,
  Location,
  LocationType,
  MaskedEvent,
  MessageEnvelope,
  Multilingual,
  OnlineMeetingInfo,
  Participant,
  ParticipantNotification,
  Participants,
  ParticipantsStatus,
  Permission,
  PlacementType,
  Properties,
  PropertiesChange,
  Provider,
  ProviderProviderInfoOneOf,
  ProviderType,
  QueryEventsOptions,
  QueryEventsRequest,
  QueryEventsResponse,
  RecurrenceRule,
  RecurrenceType,
  RecurringEventSplit,
  RequestedFields,
  ResolutionMethod,
  Resource,
  RestoreEventDefaultsOptions,
  RestoreEventDefaultsRequest,
  RestoreEventDefaultsResponse,
  RestoreInfo,
  Role,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  SplitRecurringEventOptions,
  SplitRecurringEventRequest,
  SplitRecurringEventResponse,
  Status,
  SupportedLanguage,
  TimePeriod,
  Translation,
  Transparency,
  Type,
  UpdateEvent,
  UpdateEventOptions,
  UpdateEventParticipantsRequest,
  UpdateEventParticipantsResponse,
  UpdateEventRequest,
  UpdateEventResponse,
  UpdateEventsWithFixedBusinessResourceIdRequest,
  UpdateEventsWithFixedBusinessResourceIdResponse,
  UpdateScheduleWithFixedBusinessResourceIdRequest,
  UpdateScheduleWithFixedBusinessResourceIdResponse,
  WebhookIdentityType,
  ZonedDate,
  utils,
} from './calendar-v3-event-events.universal.js';
